import { useCallback, useMemo, useState } from 'react';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { useTranslation } from '@ibk/shared-translation';
import { FeatureFlagUseCase } from '@ibk/shared-types';
import { useCnpjAlphanumeric } from '@ibk/shared-ui';
import { ApiErrors, PixType } from '../../enums';
import { useRegisterPixKeyMutation, useRegisterPixKeyV2Mutation } from '../../services/pixKeysApi';
import { unformatValue } from '../../utils/pixFormatters';
import { usePinModal } from '../usePinModal';
const TOTAL_STEPS = 3;
const FIRST_STEP = 0;
const LAST_STEP = TOTAL_STEPS - 1;
const getErrorCode = (error) => {
    const errorObj = error;
    return errorObj?.result?.error ?? null;
};
const ERROR_MESSAGE_MAP = {
    PIX_KEY_ALREADY_EXISTS: 'pix-keys.already-exists-error',
    DEFAULT: 'pix-keys.creation-error',
};
export function usePixKeyCreation(props) {
    const { t } = useTranslation();
    const { onBackToList, onBackToListWithError, initialConfirmationData, pixKeys } = props ?? {};
    const [currentStep, setCurrentStep] = useState(FIRST_STEP);
    const [selectedKeyType, setSelectedKeyType] = useState(initialConfirmationData?.keyType);
    const [keyValue, setKeyValue] = useState(initialConfirmationData?.createdKey ?? '');
    const [isInputValid, setIsInputValid] = useState(false);
    const [isInputTouched, setIsInputTouched] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(!!initialConfirmationData);
    const [protocolId, setProtocolId] = useState(initialConfirmationData?.protocolId ?? null);
    const [createdKey, setCreatedKey] = useState(initialConfirmationData?.createdKey ?? null);
    const [creationError, setCreationError] = useState(null);
    const { isFeatureFlagWhiteListed } = useFeatureFlag(undefined, { autoLoad: false });
    const isBaasV2 = isFeatureFlagWhiteListed(FeatureFlagUseCase.CORE_BANKING_ENABLE_BAAS_V2);
    const { unmaskCnpj } = useCnpjAlphanumeric();
    const [registerPixKey] = useRegisterPixKeyMutation();
    const [registerPixKeyV2] = useRegisterPixKeyV2Mutation();
    const handlePinConfirm = useCallback(async (pin) => {
        if (!selectedKeyType)
            return;
        const MAX_PIX_KEYS = 5;
        const currentKeysCount = pixKeys?.length ?? 0;
        if (currentKeysCount >= MAX_PIX_KEYS) {
            onBackToList?.(false);
            return;
        }
        try {
            let formattedKeyValue = keyValue;
            if (selectedKeyType === PixType.RANDOM) {
                formattedKeyValue = '';
            }
            else if (selectedKeyType === PixType.PHONE) {
                formattedKeyValue = `+55${unformatValue(keyValue)}`;
            }
            else if (selectedKeyType === PixType.TAX_IDENTIFIER) {
                formattedKeyValue = unmaskCnpj(keyValue);
            }
            const request = {
                credential: pin,
                key: formattedKeyValue,
                pixType: selectedKeyType,
            };
            const registerFn = isBaasV2 ? registerPixKeyV2 : registerPixKey;
            const result = await registerFn(request).unwrap();
            const responseData = result.result ?? result;
            const isConfirmationRequired = selectedKeyType === PixType.EMAIL || selectedKeyType === PixType.PHONE;
            if (isConfirmationRequired && responseData.protocolId) {
                setProtocolId(responseData.protocolId);
                setCreatedKey(responseData.key);
                setShowConfirmation(true);
            }
            else {
                setCurrentStep(0);
                onBackToList?.(true);
            }
        }
        catch (error) {
            const errorCode = getErrorCode(error);
            if (errorCode === ApiErrors.PIN_FAILURE_TO_VALIDATE) {
                throw error;
            }
            const errorMessage = errorCode ? t(ERROR_MESSAGE_MAP[errorCode]) : t(ERROR_MESSAGE_MAP.DEFAULT);
            setCreationError(errorMessage);
            onBackToListWithError?.(errorMessage);
        }
    }, [
        selectedKeyType,
        keyValue,
        isBaasV2,
        registerPixKeyV2,
        registerPixKey,
        onBackToList,
        pixKeys,
        t,
        onBackToListWithError,
        unmaskCnpj,
    ]);
    const { showPinModal, isCreating, showPinError, pinErrorMessage, handlePinConfirm: pinModalHandlePinConfirm, handlePinCancel, handlePinClose, handlePinErrorClose, openPinModal, } = usePinModal({
        onConfirm: handlePinConfirm,
    });
    const steps = useMemo(() => [
        {
            title: t('pix-keys.creation.step-1-title'),
        },
        {
            title: t('pix-keys.creation.step-2-title'),
        },
        {
            title: t('pix-keys.creation.step-3-title'),
        },
    ], [t]);
    const getNavigationButtonsClassName = useCallback(() => {
        return currentStep === 1 ? 'navigationButtonsInternal' : 'navigationButtons';
    }, [currentStep]);
    const handleKeyTypeSelect = useCallback((type) => {
        setSelectedKeyType(type);
        setIsInputValid(false);
        setIsInputTouched(false);
    }, []);
    const handleKeyValueChange = useCallback((value) => {
        setKeyValue(value);
    }, []);
    const handleValidationChange = useCallback((isValid) => {
        setIsInputValid(isValid);
    }, []);
    const handleInputTouched = useCallback(() => {
        setIsInputTouched(true);
    }, []);
    const handleNextClick = useCallback((onBackToList) => {
        if (currentStep === LAST_STEP) {
            if (onBackToList) {
                onBackToList();
            }
            return;
        }
        if (currentStep === 1) {
            const isAutoEnabledType = selectedKeyType === PixType.TAX_IDENTIFIER || selectedKeyType === PixType.RANDOM;
            const isConfirmationRequiredType = selectedKeyType === PixType.EMAIL || selectedKeyType === PixType.PHONE;
            if (isAutoEnabledType || isConfirmationRequiredType) {
                openPinModal();
                return;
            }
        }
        setCurrentStep(prevStep => {
            if (prevStep < LAST_STEP) {
                return prevStep + 1;
            }
            return prevStep;
        });
    }, [currentStep, selectedKeyType, openPinModal]);
    const handleBackClick = useCallback((onBackToList) => {
        if (onBackToList) {
            onBackToList();
        }
        else {
            setCurrentStep(prevStep => {
                if (prevStep > 0) {
                    return prevStep - 1;
                }
                return prevStep;
            });
        }
        setShowConfirmation(false);
    }, []);
    const getCanProceed = useCallback(() => {
        if (currentStep === 0) {
            return selectedKeyType !== undefined;
        }
        const isAutoEnabledType = selectedKeyType === PixType.TAX_IDENTIFIER || selectedKeyType === PixType.RANDOM;
        if (isAutoEnabledType) {
            return true;
        }
        return isInputValid;
    }, [currentStep, selectedKeyType, isInputValid]);
    const canProceed = getCanProceed();
    const shouldShowPixKeyTypeSelector = currentStep === 0;
    const shouldShowPixKeyInput = currentStep === 1;
    const shouldShowPlaceholder = currentStep === 1 && selectedKeyType === undefined;
    const placeholderText = t('pix-keys.placeholder.select-key-type');
    const handleConfirmationSuccess = useCallback(() => {
        setShowConfirmation(false);
        setProtocolId(null);
        setCreatedKey(null);
        setCurrentStep(0);
        onBackToList?.(true);
    }, [onBackToList]);
    const handleConfirmationError = useCallback((error) => {
        setCreationError(error);
        onBackToListWithError?.(error);
    }, [onBackToListWithError]);
    const handleConfirmationBack = useCallback(() => {
        setShowConfirmation(false);
        setProtocolId(null);
        setCreatedKey(null);
        onBackToList?.();
    }, [onBackToList]);
    const handleClearCreationError = useCallback(() => {
        setCreationError(null);
    }, []);
    return {
        currentStep,
        selectedKeyType,
        keyValue,
        isInputValid,
        isInputTouched,
        canProceed,
        handleKeyTypeSelect,
        handleKeyValueChange,
        handleValidationChange,
        handleInputTouched,
        handleNextClick,
        handleBackClick,
        steps,
        getNavigationButtonsClassName,
        shouldShowPixKeyTypeSelector,
        shouldShowPixKeyInput,
        shouldShowPlaceholder,
        placeholderText,
        showPinModal,
        showPinError,
        pinErrorMessage,
        showConfirmation,
        isCreating,
        protocolId,
        createdKey,
        creationError,
        handlePinConfirm: pinModalHandlePinConfirm,
        handlePinCancel,
        handlePinClose,
        handlePinErrorClose,
        handleConfirmationSuccess,
        handleConfirmationError,
        handleConfirmationBack,
        handleClearCreationError,
    };
}
